import { useQuery } from "convex/react";

import { api } from "@convex/_generated/api";

/**
 * The chat UI's onboarding MODE — the client-side mirror of the agent's
 * BOOTSTRAP state (frontend-doctrine §4.16).
 *
 * `"loading"` is a real, distinct state, NOT a synonym for either end. The
 * boolean `onboardingComplete` maps loading to `false` (fail-closed, correct
 * for the cosmetic starter chips: never flash chips at a user mid-ritual).
 * That direction is WRONG for the locks: a lock that engages on unknown status
 * takes the New Chat button away from an onboarded user for a round trip. Locks
 * therefore gate on `mode === "onboarding"` and nothing else.
 */
export type OnboardingMode = "loading" | "onboarding" | "ready";

export interface OnboardingStatus {
  /**
   * `true` only when the backend has confirmed onboarding is finished.
   *
   * Loading (`undefined` query) and unauthenticated (`null`) both resolve to
   * `false`. That is the fail-safe direction: callers gate the starter chips
   * on `=== true`, so an unknown status hides them rather than flashing them
   * at a user who is mid-ritual.
   */
  onboardingComplete: boolean;
  isLoading: boolean;
  /**
   * `"onboarding"` ONLY when the persona row exists AND onboarding is
   * unfinished. A brand-new user whose scheduled `personas.create` has not
   * landed yet is `"ready"`, not `"onboarding"`: there is no ritual in flight
   * to lock the UI around, and the escape hatch would have nothing to skip.
   */
  mode: OnboardingMode;
}

export function useOnboardingStatus(): OnboardingStatus {
  const result = useQuery(api.onboarding.statusForCurrentUser);

  if (result === undefined) {
    return { onboardingComplete: false, isLoading: true, mode: "loading" };
  }
  // Unauthenticated is RESOLVED, not loading — and it is emphatically not
  // "mid-ritual". Signed-out users never reach the locked surfaces anyway.
  if (result === null) {
    return { onboardingComplete: false, isLoading: false, mode: "ready" };
  }

  return {
    onboardingComplete: result.onboardingComplete,
    isLoading: false,
    mode:
      result.personaExists && !result.onboardingComplete
        ? "onboarding"
        : "ready",
  };
}
